<template>
  <div class="edit-mode">
    <AgGrid
      v-if="optionsFetched"
      ref="agGrid"
      :key="rerenderTable"
      :header-height="48"
      :options="gridOptions"
      :api-request="infiniteScrollRequest"
      class="q-px-sm"
      style="grid-template-rows: 1fr"
      @row-selected="selectionChanged"
      @filter-changed="filterChanged"
      @new-rows-loaded="newRowsLoaded"
      @cell-value-changed="cellValueChanged"
      @ready="onGridReady"
      @export-loading="exportLoading = $event"
    />

    <QInnerLoading :showing="!optionsFetched">
      <QSpinnerHourglass size="3.5rem" color="primary-1" />
    </QInnerLoading>
  </div>
</template>

<script>
import * as apiTo from '@/modular-api'
// Mixins
import gridOptions from './gridOptions'
import { exportToExcel } from '@/mixins/export-to-excel'
import { registerVuexModule } from '@/mixins/register-vuex-module'
// Libraries
import { cloneDeep, debounce } from 'lodash'
import { mapState, mapGetters, mapMutations } from 'vuex'
// Components
import AgGrid from '@/ag-grid/AgGrid'

export default {
  components: {
    AgGrid
  },

  mixins: [
    gridOptions,
    exportToExcel,
    registerVuexModule('lv-subscriptions-edit-mode', () => require('./lv-subscriptions-edit-mode'))
  ],

  data: () => ({
    agGrid: null,
    justFiltered: false,
    rerenderTable: 1
  }),

  computed: {
    ...mapState('lv-subscriptions', ['metadataColumns', 'editMode']),
    ...mapState('lv-subscriptions-edit-mode', ['tableState']),

    ...mapGetters('lv-subscriptions', ['optionsFetched']),
    ...mapGetters('lv-subscriptions-edit-mode', ['numberOfSelectedRows']),
    ...mapGetters('subscriptions/subscriptionPeriods', ['vendors', 'owners']),
    ...mapGetters('entity-labels', ['departmentLabel']),

    exportLoading: {
      get() {
        return this.$store.state['lv-subscriptions'].exportLoading
      },

      set(newVal) {
        this.$store.commit('lv-subscriptions/setExportLoading', newVal)
      }
    },

    totalRows: {
      get() {
        return this.$store.state['lv-subscriptions-edit-mode'].totalRows
      },

      set(newVal) {
        this.$store.commit('lv-subscriptions-edit-mode/setTotalRows', newVal)
      }
    },

    rowsSelected: {
      get() {
        return this.$store.state['lv-subscriptions-edit-mode'].rowsSelected
      },

      set(newVal) {
        this.$store.commit('lv-subscriptions-edit-mode/setRowsSelected', newVal)
      }
    },

    selectAll: {
      get() {
        return this.$store.state['lv-subscriptions-edit-mode'].selectAll
      },

      set(newVal) {
        this.$store.commit('lv-subscriptions-edit-mode/setSelectAll', newVal)
      }
    }
  },

  watch: {
    'tableState.chipFilter': 'refreshTable',

    $route() {
      this.$store.commit('lv-subscriptions/setEditMode', false)
    },

    selectAll(newVal) {
      if (this.justFiltered) return
      this.rowsSelected = []
      this.$refs.agGrid.selectOrDeselectAll(newVal)
    },

    rowsSelected(newVal) {
      if (!this.totalRows) return

      if (newVal.length === this.totalRows && this.selectAll) {
        // if selectAll checkbox is checked and all rows are unchecked
        // clear selectAll and selected rows
        this.rowsSelected = []
        this.selectAll = false
      } else if (newVal.length === this.totalRows && !this.selectAll) {
        // if all rows are selected check the selectAll checkbox
        this.selectAll = true
      }
    },

    vendors: {
      immediate: true,
      handler: 'setVendors'
    },

    owners: {
      immediate: true,
      handler: 'setOwners'
    }
  },

  created() {
    this.$store.dispatch('lv-subscriptions/getOptions')
    this.$store.dispatch('lv-subscriptions-edit-mode/setDefaultTableState')
  },

  methods: {
    ...mapMutations('lv-subscriptions-edit-mode', [
      'setVendors',
      'setOwners',
      'setTableStateFilter',
      'setTableStateColumns'
    ]),

    onGridReady(agGrid) {
      this.$emit('ag-grid', agGrid)
      this.agGrid = agGrid
      this.tableState.filter && this.agGrid.api.setFilterModel(this.tableState.filter)
      this.tableState.columns && this.agGrid.columnApi.setColumnState(this.tableState.columns)
    },

    infiniteScrollRequest(request) {
      return apiTo.GET_PAGINATED_SUBSCRIPTIONS(request, this.tableState.chipFilter).then(response => {
        // FE HACK because BE doesn't returns meta_data field if there is not a value
        response.data.result.data.forEach(this.addMetadataIfItsMissing)

        return response
      })
    },

    addMetadataIfItsMissing(row) {
      this.metadataColumns.forEach(column => {
        if (!row.meta_data.hasOwnProperty([column.system_name])) {
          if (column.type.name === 'select') row.meta_data[column.system_name] = []
          else row.meta_data[column.system_name] = null
        }
      })
    },

    exportToExcel() {
      this.mixinExportToExcel(this.agGrid, this.tableState.chipFilter, 'subscriptions', null, '0')
    },

    filterChanged() {
      this.justFiltered = true
      this.selectAll = false
      this.rowsSelected = []
    },

    selectionChanged: debounce(function({ row, isSelected }) {
      const clonedRow = cloneDeep(row)

      if (this.selectAll) {
        if (!isSelected) this.rowsSelected = [...this.rowsSelected, clonedRow]
        else this.rowsSelected = this.rowsSelected.filter(selectedRow => selectedRow.id !== row.id)
      } else {
        if (!isSelected) this.rowsSelected = this.rowsSelected.filter(selectedRow => selectedRow.id !== row.id)
        else this.rowsSelected = [...this.rowsSelected, clonedRow]
      }
    }, 0),

    newRowsLoaded({ totalRows, newRows }) {
      const newRowsIds = newRows.map(row => row.id)

      this.totalRows = totalRows
      if (this.justFiltered) {
        this.agGrid.api.forEachNode(node => {
          if (newRowsIds.includes(node.id)) node.setSelected(false)
        })
        this.justFiltered = false
      } else if (this.selectAll) {
        this.agGrid.api.forEachNode(node => {
          if (newRowsIds.includes(node.id)) node.setSelected(true)
        })
      }
    },

    didValueChanged(old, newVal) {
      if (typeof old === 'string' && typeof newVal === 'number') return Number(old) !== Number(newVal)
      return old !== newVal
    },

    cellValueChanged(event) {
      if (!this.didValueChanged(event.oldValue, event.newValue)) return

      const newValue = this.getValueForColumn(event.newValue, event.column.colId)

      apiTo
        .MODIFY_SUBSCRIPTION_PERIOD(event.data.id, { [this.getColumnName(event.column.colId)]: newValue }, [
          'status',
          'subscription.product.type',
          'departments',
          'stakeholders',
          'vendor',
          'publisher',
          'owner',
          'metaData',
          'financialPeriod'
        ])
        .then(response => {
          const row = response.data.result
          this.addMetadataIfItsMissing(row)
          const colsToRefreshTable = ['product_type', 'library_reference', 'publisher_reference']

          if (colsToRefreshTable.includes(event.column.colId)) this.refreshTable()
          else this.$refs.agGrid.updateRow(event.data.id, row)
        })
        .catch(() => {
          let oldValue = { [event.column.colId]: event.oldValue }

          if (event.column.colId.startsWith('meta_data')) {
            oldValue = {
              meta_data: {
                ...event.data.meta_data,
                [event.column.colId.split('.')[1]]: event.oldValue
              }
            }
          }

          this.$refs.agGrid.updateRow(event.data.id, {
            ...event.data,
            ...oldValue
          })
        })
    },

    getColumnName(colId) {
      if (colId.startsWith('meta_data')) return 'meta_data'

      const columnsMap = {
        vendor: 'vendor_id',
        publisher: 'publisher_id',
        owner: 'owner_id',
        departments: 'departments_ids',
        stakeholders: 'stakeholders_ids'
      }

      if (colId in columnsMap) return columnsMap[colId]
      return colId
    },

    getValueForColumn(value, column) {
      if (['departments', 'stakeholders'].includes(column)) return value.map(v => v.id)
      if (column.startsWith('meta_data')) {
        const system_name = column.split('.')[1]
        const metadata = this.metadataColumns.find(m => m.system_name === system_name)

        return { id: metadata.id, is_required: metadata.is_required, values: value }
      }

      return value
    },

    refreshTable() {
      if (!this.agGrid) return

      const filterModel = this.agGrid.api.getFilterModel()
      const columnState = this.agGrid.columnApi.getColumnState()

      this.setTableStateFilter(filterModel)
      this.setTableStateColumns(columnState)

      this.rerenderTable++
    }
  }
}
</script>
